import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Red pin for non-selected hubs
const defaultIcon = new L.Icon({
    iconUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23EA4335' width='36' height='36'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E",
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28]
});

// Black needle icon for selected hub
const selectedIcon = new L.Icon({
    iconUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath d='M16 30 L16 14' stroke='%23333' stroke-width='2'/%3E%3Ccircle cx='16' cy='10' r='7' fill='%23000000' stroke='white' stroke-width='1'/%3E%3Ccircle cx='14' cy='8' r='2' fill='white' opacity='0.4'/%3E%3C/svg%3E",
    iconSize: [32, 32],
    iconAnchor: [16, 30],
    popupAnchor: [0, -30]
});

// Blue pin for "Your position" (matching hero blue)
const userIcon = new L.Icon({
    iconUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%232563eb' width='48' height='48'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E",
    iconSize: [38, 38],
    iconAnchor: [19, 38],
    popupAnchor: [0, -38]
});

// Refined grey needle icon for other hubs (foncé top, claire stick)
const secondaryIcon = new L.Icon({
    iconUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath d='M16 30 L16 14' stroke='%239ca3af' stroke-width='2'/%3E%3Ccircle cx='16' cy='10' r='7' fill='%236b7280' stroke='white' stroke-width='1'/%3E%3Ccircle cx='14' cy='8' r='2' fill='white' opacity='0.4'/%3E%3C/svg%3E",
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28]
});

// Purple needle icon for photo-print hubs (Photo Printing Hub)
const photoPrintIcon = new L.Icon({
    iconUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath d='M16 30 L16 14' stroke='%237e22ce' stroke-width='2'/%3E%3Ccircle cx='16' cy='10' r='7' fill='%239333ea' stroke='white' stroke-width='1'/%3E%3Ccircle cx='14' cy='8' r='2' fill='white' opacity='0.4'/%3E%3C/svg%3E",
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28]
});

// Highlighted Purple needle icon for selected / nearest photo-print hub
const photoPrintSelectedIcon = new L.Icon({
    iconUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath d='M16 30 L16 14' stroke='%23581c87' stroke-width='2.5'/%3E%3Ccircle cx='16' cy='10' r='8' fill='%239333ea' stroke='white' stroke-width='1.5'/%3E%3Ccircle cx='14' cy='8' r='2' fill='white' opacity='0.6'/%3E%3C/svg%3E",
    iconSize: [32, 32],
    iconAnchor: [16, 30],
    popupAnchor: [0, -30]
});

// Modern minimalist SVG Icons to replace emojis
const Icons = {
    Phone: ({ size = 14, color = "currentColor" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
    MapPin: ({ size = 14, color = "currentColor" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>,
    Globe: ({ size = 14, color = "currentColor" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>,
    Star: ({ size = 14, color = "currentColor", fill = "none" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>,
    Mail: ({ size = 14, color = "currentColor" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>,
    User: ({ size = 14, color = "currentColor" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
    X: ({ size = 18, color = "currentColor" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
    Tent: ({ size = 14, color = "currentColor" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 20 10 4" /><path d="m5 20 9-16" /><path d="M3 20h18" /><path d="m12 15-3 5" /><path d="m12 15 3 5" /></svg>,
    Map: ({ size = 14, color = "currentColor" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" /><line x1="9" y1="3" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="21" /></svg>,
    CircleDot: ({ size = 12, color = "currentColor" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /></svg>,
    Walking: ({ size = 14, color = "currentColor" }) => (
        <svg fill={color} width={size} height={size} viewBox="0 0 14 14" role="img" focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <path d="M 7.3,5.2 8.1657334,6.4986 C 8.3528566,6.779285 8.771146,7 9.105621,7 L 10.9,7 10.9,5.8 9.1,5.8 8.2342672,4.5014 C 8.0471434,4.220715 7.6755154,3.850344 7.3985998,3.665733 L 6.6014004,3.134267 C 6.3207153,2.9471436 5.869105,2.938537 5.5842816,3.109431 L 3.1,4.6 l 0,3 1.2,0 0,-2.4 L 5.5,4.6 3.7,13 4.9,13 6.3117647,8.411765 7.3,9.4 7.3,13 8.5,13 8.5,8.2 6.8764708,6.576471 7.3,5.2 Z M 7.9,3.4 C 8.562742,3.4 9.1,2.8627417 9.1,2.2 9.1,1.5372583 8.562742,1 7.9,1 7.2372586,1 6.7,1.5372583 6.7,2.2 c 0,0.6627417 0.5372586,1.2 1.2,1.2 l 0,0 z" />
        </svg>
    )
};

const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

// Fit all markers in view on first load
const MapController = ({ markers, selectedCoords, hasSelected, nearestOnly }) => {
    const map = useMap();
    const fittedRef = useRef(false);
    const isFirstSelection = useRef(true);

    // Invalidate size on mount and whenever markers change to prevent "gray map" issue
    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize();
        }, 100);
        return () => clearTimeout(timer);
    }, [map, markers]);

    useEffect(() => {
        if (markers.length > 0 && !fittedRef.current) {
            // Prioritize user and nearest/selected hub for initial zoom
            const priorityMarkers = markers.filter(m => m.isUser || m.isSelected || m.isNearest);
            const targetMarkers = priorityMarkers.length >= 2 ? priorityMarkers : markers;
            
            const bounds = L.latLngBounds(targetMarkers.map(m => m.coords));

            // If we have both user location and hub location
            if (targetMarkers.some(m => m.isUser) && targetMarkers.some(m => !m.isUser)) {
                // Fit bounds to show both user and nearest hub clearly
                map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
                fittedRef.current = true;
            } else {
                map.fitBounds(bounds, { padding: [30, 30], maxZoom: 7 });
                fittedRef.current = true;
            }
        }
    }, [markers, map]);

    useEffect(() => {
        if (hasSelected && selectedCoords && !nearestOnly) {
            if (isFirstSelection.current) {
                isFirstSelection.current = false;
                // Ignore the first zoom to keep the initial map view broader
            } else {
                map.setView(selectedCoords, 18, { animate: true, duration: 0.6 });
            }
        }
    }, [selectedCoords, hasSelected, map, nearestOnly]);

    return null;
};

// Build Google Maps URL (simple format)
const getGoogleMapsUrl = (hub) => {
    const coords = getHubCoordinates(hub);
    if (coords) {
        return `https://www.google.com/maps?q=${coords[0]},${coords[1]}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent('QPH-' + hub.address + ' ' + hub.city)}`;
};

// Get today's operating hours for a hub
const getTodayHours = (hub) => {
    if (!hub.operatingHours || !Array.isArray(hub.operatingHours)) return null;
    const now = new Date();
    const dayIndex = now.getDay(); // 0=Sunday ... 6=Saturday
    const todayHours = hub.operatingHours.find(oh => oh.day === dayIndex);
    if (!todayHours) return null;
    if (todayHours.isClosed) return { isOpen: false, text: 'Closed today' };
    if (!todayHours.openTime || !todayHours.closeTime) return { isOpen: false, text: 'Closed today' };

    // Check if currently open
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const [openH, openM] = todayHours.openTime.split(':').map(Number);
    const [closeH, closeM] = todayHours.closeTime.split(':').map(Number);
    const openMin = openH * 60 + openM;
    const closeMin = closeH * 60 + closeM;

    let isOpen = false;
    if (openMin < closeMin) {
        isOpen = currentMinutes >= openMin && currentMinutes < closeMin;
    } else {
        isOpen = currentMinutes >= openMin || currentMinutes < closeMin;
    }

    return {
        isOpen,
        text: `${todayHours.openTime} - ${todayHours.closeTime}`
    };
};

// Extract coordinates helper (module-level for reuse)
// Extract coordinates helper (module-level for reuse)
function getHubCoordinates(hub) {
    if (!hub) return null;
    // Method 1: coordinates object
    if (hub.coordinates?.latitude && hub.coordinates?.longitude) {
        return [parseFloat(hub.coordinates.latitude), parseFloat(hub.coordinates.longitude)];
    }
    if (hub.coordinates?.lat && hub.coordinates?.lng) {
        return [parseFloat(hub.coordinates.lat), parseFloat(hub.coordinates.lng)];
    }
    // Method 2: latitude/longitude naming
    if (hub.latitude && hub.longitude) {
        return [parseFloat(hub.latitude), parseFloat(hub.longitude)];
    }
    // Method 3: lat/lng or lat/lon naming
    if (hub.lat && (hub.lng || hub.lon)) {
        return [parseFloat(hub.lat), parseFloat(hub.lng || hub.lon)];
    }
    // Method 4: locationData object
    if (hub.locationData?.latitude && hub.locationData?.longitude) {
        return [parseFloat(hub.locationData.latitude), parseFloat(hub.locationData.longitude)];
    }
    if (hub.locationData?.lat && hub.locationData?.lng) {
        return [parseFloat(hub.locationData.lat), parseFloat(hub.locationData.lng)];
    }
    return null;
}

const HubMap = ({ selectedStudio, onStudioSelect, selectedCountry, nearestOnly = false, t }) => {
    const [hubs, setHubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showHubList, setShowHubList] = useState(false);
    const [activeHub, setActiveHub] = useState(null); // Hub whose info card is shown
    const [nearestHubId, setNearestHubId] = useState(null); // Track nearest hub specifically
    const [infoDismissed, setInfoDismissed] = useState(false); // Track if user closed the info card
    const [userLocation, setUserLocation] = useState(null);
    const fetchedCountryRef = useRef(null);

    const normalizeCountryCode = (code) => {
        if (!code) return '';
        const normalized = code.toLowerCase().trim();
        const countryMap = {
            'ca': 'canada', 'can': 'canada', 'canada': 'canada',
            'tn': 'tunisia', 'tun': 'tunisia', 'tunisia': 'tunisia'
        };
        return countryMap[normalized] || normalized;
    };

    // Fetch hubs and user location in parallel
    useEffect(() => {
        const normalizedCountry = normalizeCountryCode(selectedCountry);
        
        const fetchData = async () => {
            setLoading(true);

            // Start fetchHubs and geolocation/IP-fallback in parallel
            const fetchHubsPromise = (async () => {
                const response = await fetch(
                    `https://freezepix-database-server-c95d4dd2046d.herokuapp.com/api/studios?country=${normalizedCountry}`
                );
                if (!response.ok) throw new Error('Failed');
                const data = await response.json();
                let arr;
                if (Array.isArray(data)) arr = data;
                else if (data.success && Array.isArray(data.studios)) arr = data.studios;
                else if (Array.isArray(data.studios)) arr = data.studios;
                else arr = [];

                return arr.filter(h =>
                    !h.familiaportrais && !h.familiaportraits &&
                    !h.familyPortraits && !h.family_portraits &&
                    getHubCoordinates(h) !== null
                );
            })();

            const fetchLocationPromise = (async () => {
                if (!nearestOnly) return null;

                return new Promise((resolve) => {
                    const handleFallback = async () => {
                        console.log("📍 HubMap: Browser geolocation failed after retries. Attempting IP-based fallback...");
                        try {
                            const res = await fetch('https://get.geojs.io/v1/ip/geo.json');
                            const data = await res.json();
                            if (data && data.latitude && data.longitude) {
                                console.log("✅ HubMap: Using IP-based location fallback.");
                                resolve({ lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) });
                            } else {
                                console.log("❌ HubMap: IP-based fallback failed.");
                                resolve(null);
                            }
                        } catch (e) {
                            console.log("❌ HubMap: IP-based fallback error:", e);
                            resolve(null);
                        }
                    };

                    const attemptGeolocation = (retriesLeft) => {
                        if ("geolocation" in navigator) {
                            navigator.geolocation.getCurrentPosition(
                                pos => {
                                    console.log("✅ HubMap: Using browser geolocation.");
                                    resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                                },
                                (err) => {
                                    console.warn(`📍 HubMap: Geolocation attempt failed (code: ${err.code}, message: ${err.message}). Retries left: ${retriesLeft - 1}`);
                                    if (retriesLeft > 1) {
                                        setTimeout(() => attemptGeolocation(retriesLeft - 1), 1000);
                                    } else {
                                        handleFallback();
                                    }
                                },
                                { timeout: 5000, maximumAge: 0, enableHighAccuracy: true }
                            );
                        } else {
                            console.log("📍 HubMap: Geolocation not supported by browser. Using IP-based fallback...");
                            handleFallback();
                        }
                    };

                    attemptGeolocation(3);
                });
            })();

            try {
                const [validHubs, location] = await Promise.all([fetchHubsPromise, fetchLocationPromise]);

                if (location) {
                    setUserLocation(location);
                    let nearest = null;
                    let minDistance = Infinity;

                    validHubs.forEach(h => {
                        const c = getHubCoordinates(h);
                        if (c) {
                            const dist = getDistance(location.lat, location.lng, c[0], c[1]);
                            if (dist < minDistance) {
                                minDistance = dist;
                                nearest = h;
                            }
                        }
                    });

                    setHubs(validHubs); // Always set all hubs
                    
                    if (nearest) {
                        setNearestHubId(nearest._id);
                        if (!selectedStudio) setActiveHub(nearest);
                    } else if (validHubs.length > 0) {
                        setActiveHub(validHubs[0]);
                    }
                } else {
                    // No location found or allowed
                    setHubs(validHubs);
                    if (validHubs.length > 0) setActiveHub(validHubs[0]);
                }
            } catch (err) {
                console.error('Error in map data initialization:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedCountry, nearestOnly]);

    const handleSelectHub = useCallback((hub) => {
        if (onStudioSelect) onStudioSelect(hub);
        setActiveHub(hub);
        setInfoDismissed(false);
        try {
            localStorage.setItem('HubSelected', JSON.stringify(hub));
            localStorage.setItem('isPreselectedFromUrl', 'false');
        } catch (e) { /* ignore */ }
    }, [onStudioSelect]);

    const markers = useMemo(() => {
        const coordMap = new Map(); // To track overlapping markers

        const generatedMarkers = hubs
            .map(hub => {
                const rawCoords = getHubCoordinates(hub);
                if (!rawCoords || isNaN(rawCoords[0]) || isNaN(rawCoords[1])) return null;

                // Simple jitter logic for overlapping markers
                const coordKey = `${rawCoords[0].toFixed(6)},${rawCoords[1].toFixed(6)}`;
                const count = coordMap.get(coordKey) || 0;
                coordMap.set(coordKey, count + 1);

                let coords = rawCoords;
                if (count > 0) {
                    // Apply a tiny offset if multiple markers share exact same location
                    const angle = (count * 137.5) * (Math.PI / 180); // Fibonacci spiral angle
                    const radius = 0.0001 * Math.sqrt(count); // Small radius in degrees
                    coords = [
                        rawCoords[0] + radius * Math.cos(angle),
                        rawCoords[1] + radius * Math.sin(angle)
                    ];
                }

                const isSelected = selectedStudio && hub._id === selectedStudio._id;
                const isNearest = hub._id === nearestHubId;
                return { hub, coords, isSelected, isNearest, isUser: false };
            })
            .filter(Boolean);

        if (userLocation) {
            generatedMarkers.push({
                hub: null,
                coords: [userLocation.lat, userLocation.lng],
                isSelected: false,
                isNearest: false,
                isUser: true,
                id: "user-loc"
            });
        }
        return generatedMarkers;
    }, [hubs, selectedStudio, nearestHubId, userLocation]);

    const selectedCoords = useMemo(() => {
        if (!selectedStudio && !activeHub) return null;
        return getHubCoordinates(selectedStudio || activeHub);
    }, [selectedStudio, activeHub]);

    // Set activeHub when selectedStudio changes (only if not dismissed)
    useEffect(() => {
        if (selectedStudio && !infoDismissed) setActiveHub(selectedStudio);
    }, [selectedStudio, infoDismissed]);

    // --- Styles ---
    const mapHeight = nearestOnly ? 'min(45vh, 400px)' : 'min(30vh, 240px)';

    if (loading) {
        return (
            <div style={{
                height: mapHeight, width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#f8f9fa'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: '32px', height: '32px',
                        border: '3px solid #e2e8f0', borderTopColor: '#3b82f6',
                        borderRadius: '50%', animation: 'hubSpin .8s linear infinite',
                        margin: '0 auto 12px'
                    }} />
                    <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500', fontFamily: "'Inter', sans-serif" }}>
                        {t ? t('map.loading') : 'Loading locations...'}
                    </div>
                </div>
                <style>{`@keyframes hubSpin{to{transform:rotate(360deg)}}`}</style>
            </div>
        );
    }

    if (hubs.length === 0) return null;

    const defaultCenter = markers.length > 0 ? markers[0].coords : [43.65, -79.38];
    const displayHub = activeHub;

    return (
        <div style={{ width: '100%', position: 'relative', fontFamily: "'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
            {/* Map */}
            <div style={{
                height: mapHeight, width: '100%',
                position: 'relative',
                zIndex: 1
            }}>
                <MapContainer
                    center={defaultCenter}
                    zoom={7}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={true}
                    scrollWheelZoom={false}
                    dragging={true}
                    touchZoom={true}
                    doubleClickZoom={true}
                    attributionControl={false}
                    maxZoom={20}
                >
                    <TileLayer
                        url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                        attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>'
                        maxZoom={20}
                    />
                    <MapController
                        markers={markers}
                        selectedCoords={selectedCoords}
                        hasSelected={!!selectedStudio}
                        nearestOnly={nearestOnly}
                    />

                    {markers.map(({ hub, coords, isSelected, isNearest, isUser, id }) => {
                        if (isUser) {
                            return (
                                <Marker
                                    key={id}
                                    position={coords}
                                    icon={userIcon}
                                    zIndexOffset={2000}
                                >
                                    <Popup className="custom-popup">
                                        <div style={{ fontFamily: 'inherit', fontWeight: '600' }}>
                                            {t ? t('map.legend.user') : 'Your position'}
                                        </div>
                                    </Popup>
                                </Marker>
                            );
                        }

                        const isPhotoPrint = hub.type === 'photo-print';
                        const markerIcon = isPhotoPrint
                            ? ((isSelected || isNearest) ? photoPrintSelectedIcon : photoPrintIcon)
                            : ((isSelected || isNearest) ? selectedIcon : secondaryIcon);

                        return (
                            <Marker
                                key={hub._id}
                                position={coords}
                                icon={markerIcon}
                                zIndexOffset={isSelected || isNearest ? 1000 : 0}
                                eventHandlers={{
                                    click: () => handleSelectHub(hub)
                                }}
                            >
                                <Popup className="custom-popup">
                                    <div style={{ minWidth: '220px', fontFamily: 'inherit' }}>
                                        <div style={{ fontWeight: '700', fontSize: '15px', color: '#111827', marginBottom: '4px' }}>
                                            {hub.name}
                                        </div>
                                        {isPhotoPrint && (
                                            <div style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                borderRadius: '12px',
                                                background: '#f3e8ff',
                                                color: '#7e22ce',
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                marginBottom: '8px'
                                            }}>
                                                📷 {t ? t('map.legend.photoHub') : 'Photo Printing Hub'}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '13px', color: '#4b5563', marginBottom: '10px', lineHeight: '1.4' }}>
                                            {hub.address}, {hub.city}
                                        </div>
                                        {(hub.owner?.phone || hub.phone) && (
                                            <a href={`tel:${hub.owner?.phone || hub.phone}`}
                                                style={{ fontSize: '13px', color: '#2563eb', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: '500' }}>
                                                <Icons.Phone size={14} /> {hub.owner?.phone || hub.phone}
                                            </a>
                                        )}
                                        {(() => {
                                            const hours = getTodayHours(hub);
                                            if (!hours) return null;
                                            return (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: hours.isOpen ? '#059669' : '#dc2626', marginBottom: '12px', fontWeight: '600' }}>
                                                    <Icons.CircleDot size={10} color={hours.isOpen ? '#059669' : '#dc2626'} />
                                                    {hours.isOpen ? (t ? t('map.open') : 'Open') : (t ? t('map.closed') : 'Closed')} · <span style={{ color: '#6b7280', fontWeight: 'normal' }}>{hours.text}</span>
                                                </div>
                                            );
                                        })()}
                                        <a href={getGoogleMapsUrl(hub)} target="_blank" rel="noopener noreferrer"
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600',
                                                color: '#111827', background: '#f3f4f6', borderRadius: '20px',
                                                padding: '8px 16px', textDecoration: 'none', border: '1px solid #e5e7eb',
                                                transition: 'background 0.2s', width: '100%', justifyContent: 'center'
                                            }}>
                                            <Icons.Map size={14} /> Google Maps
                                        </a>
                                    </div>
                                </Popup>
                            </Marker>
                        )
                    })}

                    {/* Route Line */}
                    {nearestOnly && userLocation && activeHub && (
                        <Polyline
                            positions={[
                                [userLocation.lat, userLocation.lng],
                                getHubCoordinates(activeHub)
                            ]}
                            pathOptions={{
                                color: '#2563eb',
                                weight: 3,
                                dashArray: '5, 10',
                                opacity: 0.6
                            }}
                        />
                    )}
                </MapContainer>

                {/* Hubs count - styling improvements */}
                {!nearestOnly && (
                    <div
                        onClick={() => setShowHubList(!showHubList)}
                        style={{
                            position: 'absolute', bottom: '16px', right: '16px', zIndex: 1000,
                            background: '#ffffff', padding: '8px 16px', borderRadius: '24px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            fontSize: '14px', fontWeight: '600', color: '#111827',
                            cursor: 'pointer', userSelect: 'none',
                            display: 'flex', alignItems: 'center', gap: '8px',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            border: '1px solid rgba(0,0,0,0.05)'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; }}
                    >
                        <Icons.MapPin size={16} color="#3b82f6" />
                        {showHubList ? (t ? t('map.closeList') : 'Close list') : `${hubs.length} ${t ? (hubs.length !== 1 ? t('map.locations') : t('map.location')) : ('Location' + (hubs.length !== 1 ? 's' : ''))}`}
                    </div>
                )}

                {/* Hub list panel - Floating styled list */}
                {!nearestOnly && showHubList && (
                    <div style={{
                        position: 'absolute', bottom: '64px', right: '16px', zIndex: 1001,
                        background: '#fff', borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        width: '280px', maxHeight: '280px',
                        overflowY: 'auto', overflowX: 'hidden',
                        border: '1px solid rgba(0,0,0,0.05)',
                        animation: 'fadeInUp 0.3s ease'
                    }}>
                        <div style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f3f4f6', position: 'sticky', top: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(4px)', zIndex: 2 }}>
                            {t ? t('map.availableHubs') : 'Available Hubs'}
                        </div>
                        {hubs.map(hub => {
                            const isActive = selectedStudio && hub._id === selectedStudio._id;
                            const isPhoto = hub.type === 'photo-print';
                            const iconColor = isPhoto ? '#9333ea' : (isActive ? '#059669' : '#dc2626');
                            return (
                                <div
                                    key={hub._id}
                                    onClick={() => { handleSelectHub(hub); setShowHubList(false); }}
                                    style={{
                                        padding: '12px 16px', cursor: 'pointer',
                                        borderBottom: '1px solid #f3f4f6',
                                        background: isActive ? '#f0fdf4' : '#fff',
                                        display: 'flex', alignItems: 'flex-start', gap: '12px',
                                        transition: 'background 0.2s ease'
                                    }}
                                    onMouseOver={(e) => { if (!isActive) e.currentTarget.style.background = '#f9fafb' }}
                                    onMouseOut={(e) => { if (!isActive) e.currentTarget.style.background = '#fff' }}
                                >
                                    <span style={{ display: 'flex', marginTop: '2px' }}>
                                        <Icons.MapPin size={18} color={iconColor} />
                                    </span>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{
                                            fontSize: '14px', fontWeight: isActive ? '600' : '500',
                                            color: isActive ? '#065f46' : '#111827',
                                            marginBottom: '2px', lineHeight: '1.4'
                                        }}>{hub.name}</div>
                                        <div style={{
                                            fontSize: '12px', color: '#6b7280',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                        }}>{hub.city}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                <style>{`
                    @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                    .custom-popup .leaflet-popup-content-wrapper { border-radius: 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.12); padding: 4px; }
                    .custom-popup .leaflet-popup-content { margin: 16px; font-family: 'Inter', sans-serif; }
                    .custom-popup .leaflet-popup-tip { box-shadow: 0 8px 24px rgba(0,0,0,0.12); }
                `}</style>
            </div>

            {/* Bottom Floating Info Card */}
            {displayHub && (
                <div style={{
                    background: '#fff',
                    padding: '12px 16px',
                    boxShadow: '0 -8px 30px rgba(0,0,0,0.08)',
                    position: 'relative',
                    zIndex: 1000,
                    borderTopLeftRadius: '20px',
                    borderTopRightRadius: '20px',
                    marginTop: '-27px',
                    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                    animation: 'slideUpCard 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                }}>
                    <style>{`
                        @keyframes slideUpCard { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                    `}</style>

                    {/* Top grip indicator */}
                    <div style={{ position: 'absolute', top: '8px', left: '50%', transform: 'translateX(-50%)', width: '36px', height: '4px', background: '#e5e7eb', borderRadius: '4px' }} />

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '4px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                fontSize: '18px', fontWeight: '700', color: '#111827',
                                marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px',
                                flexWrap: 'wrap', lineHeight: '1.2'
                            }}>
                                <span>{displayHub.name}</span>
                                {displayHub.type === 'photo-print' && (
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        background: '#f3e8ff',
                                        color: '#7e22ce',
                                        fontSize: '11px',
                                        fontWeight: '700'
                                    }}>
                                        📷 {t ? t('map.legend.photoHub') : 'Photo Printing Hub'}
                                    </span>
                                )}
                            </div>
                            
                            {displayHub.businessName && displayHub.businessName !== displayHub.name && (
                                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px', fontWeight: '500' }}>
                                    {displayHub.businessName}
                                </div>
                            )}

                            <div style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.4', marginTop: '4px' }}>
                                {displayHub.address}
                            </div>

                            {nearestOnly && userLocation && (
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    marginTop: '6px', color: '#2563eb', fontWeight: '700', fontSize: '13px'
                                }}>
                                    {(() => {
                                        const coords = getHubCoordinates(displayHub);
                                        const dist = getDistance(userLocation.lat, userLocation.lng, coords[0], coords[1]);
                                        
                                        if (dist < 10) {
                                            const time = Math.round((dist / 5) * 60);
                                            return <><Icons.Walking size={16} /> ({dist.toFixed(1)} km, {time} min)</>;
                                        } else if (dist < 220) {
                                            const time = Math.round((dist / 50) * 60);
                                            return <><Icons.Map size={14} /> ({dist.toFixed(1)} km, ~{time} min)</>;
                                        } else {
                                            return <><Icons.Globe size={14} /> ({dist.toFixed(0)} km)</>;
                                        }
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action buttons row */}
                    <div style={{
                        display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: '4px',
                        msOverflowStyle: 'none', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch'
                    }}>
                        {(displayHub.owner?.phone || displayHub.phone) && (
                            <a href={`tel:${displayHub.owner?.phone || displayHub.phone}`}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '5px',
                                    fontSize: '12px', fontWeight: '600', color: '#2563eb',
                                    background: '#eff6ff', borderRadius: '20px',
                                    padding: '8px 14px', textDecoration: 'none', whiteSpace: 'nowrap',
                                    transition: 'background 0.2s', border: '1px solid #bfdbfe'
                                }}>
                                <Icons.Phone size={14} /> {displayHub.owner?.phone || displayHub.phone}
                            </a>
                        )}

                        <a href={getGoogleMapsUrl(displayHub)} target="_blank" rel="noopener noreferrer"
                            style={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                fontSize: '12px', fontWeight: '600', color: '#111827',
                                background: '#f3f4f6', borderRadius: '20px',
                                padding: '8px 14px', textDecoration: 'none', whiteSpace: 'nowrap',
                                transition: 'background 0.2s', border: '1px solid #e5e7eb'
                            }}>
                            <Icons.Map size={14} /> {t ? t('map.directions') : 'Direction'}
                        </a>

                        {displayHub.website && (
                            <a href={displayHub.website} target="_blank" rel="noopener noreferrer"
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '5px',
                                    fontSize: '12px', fontWeight: '600', color: '#111827',
                                    background: '#f3f4f6', borderRadius: '20px',
                                    padding: '8px 14px', textDecoration: 'none', whiteSpace: 'nowrap',
                                    transition: 'background 0.2s', border: '1px solid #e5e7eb'
                                }}>
                                <Icons.Globe size={14} /> {t ? t('map.website') : 'Site'}
                            </a>
                        )}
                    </div>

                    {/* Additional Details */}
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #f3f4f6', paddingTop: '12px' }}>
                        {displayHub.vacationMode && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fef2f2', padding: '8px 12px', borderRadius: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', color: '#ef4444' }}>
                                    <Icons.Tent size={16} />
                                </div>
                                <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '600' }}>
                                    {t ? t('map.onVacation') : 'On vacation'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HubMap;
